/** Logs tab — the run's normalized log rows in a compact data grid.
 *
 * Client-side multi-keyword search (whitespace-split, every keyword must
 * match — against message, level and timestamp), level colorization, and
 * inline expansion: clicking a row toggles it to full wrapped height (no
 * nested dialog — the dashboard's line-detail modal is deliberately not
 * ported); a "Wrap all" switch expands everything. Download buttons export
 * exactly the rows currently shown (filtered), as .txt or .json.
 */
import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import * as importRunsApi from '@/api/importRuns';
import type { ImportRunLogRow } from '@/api/importRuns';
import { levelColor, LinkifiedText } from '@/features/bgtasks/taskFormat';

const COLLAPSED_ROW_HEIGHT = 26;

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Highlight every keyword occurrence (case-insensitive). */
function MultiHighlight({ text, keywords }: { text: string; keywords: string[] }) {
  if (keywords.length === 0 || !text) return <>{text}</>;
  const re = new RegExp(`(${keywords.map(escapeRegExp).join('|')})`, 'ig');
  const parts = text.split(re);
  if (parts.length === 1) return <>{text}</>;
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <Box
            key={i}
            component="mark"
            sx={{ bgcolor: 'warning.light', color: 'inherit', px: 0.1, borderRadius: 0.5 }}
          >
            {part}
          </Box>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

function rowTs(row: ImportRunLogRow): string {
  return row.ts ?? [row.date, row.time].filter(Boolean).join(' ');
}

function downloadBlob(name: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export function LogsTab({ runId }: { runId: string }) {
  const [search, setSearch] = useState('');
  const [wrapAll, setWrapAll] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const query = useQuery({
    queryKey: ['import-runs', 'logs', runId],
    queryFn: () => importRunsApi.logs(runId),
    staleTime: Infinity, // finished runs' logs are immutable
  });

  const rows = useMemo(() => query.data?.logs ?? [], [query.data?.logs]);

  const keywords = useMemo(
    () => search.trim().toLowerCase().split(/\s+/).filter(Boolean),
    [search],
  );

  const filtered = useMemo(() => {
    if (keywords.length === 0) return rows;
    return rows.filter((r) => {
      const haystack = `${rowTs(r)} ${r.level ?? ''} ${r.message ?? ''}`.toLowerCase();
      return keywords.every((k) => haystack.includes(k));
    });
  }, [rows, keywords]);

  const toggleRow = useCallback((id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const columns = useMemo<GridColDef<ImportRunLogRow>[]>(() => {
    const wrapSx = {
      whiteSpace: 'pre-wrap' as const,
      wordBreak: 'break-word' as const,
      py: 0.5,
    };
    const clipSx = {
      whiteSpace: 'nowrap' as const,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    };
    return [
      {
        field: 'id',
        headerName: '#',
        width: 64,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Box component="span" sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums', fontSize: 11 }}>
            {p.row.id}
          </Box>
        ),
      },
      {
        field: 'ts',
        headerName: 'Timestamp',
        width: 175,
        sortable: false,
        filterable: false,
        valueGetter: (_v, row) => rowTs(row),
        renderCell: (p) => (
          <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>
            <MultiHighlight text={rowTs(p.row)} keywords={keywords} />
          </Box>
        ),
      },
      {
        field: 'message',
        headerName: 'Message',
        flex: 1,
        minWidth: 300,
        sortable: false,
        filterable: false,
        renderCell: (p) => {
          const isExpanded = wrapAll || expanded.has(p.row.id);
          return (
            // No Level column — the message itself carries the severity color
            // (levels still match the keyword search).
            <Box
              component="span"
              sx={{
                fontFamily: 'monospace',
                fontSize: 11,
                cursor: 'pointer',
                color: levelColor(p.row.level ?? undefined),
                ...(isExpanded ? wrapSx : clipSx),
              }}
              title={isExpanded ? undefined : 'Click to expand'}
            >
              {/* The keyword highlight takes over from LinkifiedText for the
                  matched view (same rule as the task log viewer): a line can
                  be searched or have clickable URLs, not both at once. */}
              {keywords.length > 0 ? (
                <MultiHighlight text={p.row.message ?? ''} keywords={keywords} />
              ) : (
                <LinkifiedText text={p.row.message ?? ''} />
              )}
            </Box>
          );
        },
      },
    ];
  }, [keywords, wrapAll, expanded]);

  const getRowHeight = useCallback(
    (params: { id: string | number }) =>
      wrapAll || expanded.has(Number(params.id)) ? ('auto' as const) : COLLAPSED_ROW_HEIGHT,
    [wrapAll, expanded],
  );

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (query.error || !query.data) {
    return <Alert severity="error">Couldn’t load the import log.</Alert>;
  }
  if (rows.length === 0) {
    return <Alert severity="info">No log entries are recorded for this run.</Alert>;
  }

  const filteredSuffix = keywords.length > 0 ? '-filtered' : '';
  const downloadTxt = () =>
    downloadBlob(
      `import-${runId}-logs${filteredSuffix}.txt`,
      filtered.map((r) => [rowTs(r), r.level, r.message].filter(Boolean).join(' ')).join('\n'),
      'text/plain',
    );
  const downloadJson = () =>
    downloadBlob(
      `import-${runId}-logs${filteredSuffix}.json`,
      JSON.stringify(filtered, null, 2),
      'application/json',
    );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minHeight: 0, flex: 1 }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search logs… (all keywords must match)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 340 }}
        />
        <FormControlLabel
          control={<Switch size="small" checked={wrapAll} onChange={(e) => setWrapAll(e.target.checked)} />}
          label="Wrap all"
          slotProps={{ typography: { variant: 'body2' } }}
        />
        <Typography variant="body2" color="text.secondary">
          {filtered.length.toLocaleString()} of {rows.length.toLocaleString()} lines
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<DownloadIcon />} onClick={downloadTxt}>
          .txt
        </Button>
        <Button size="small" startIcon={<DownloadIcon />} onClick={downloadJson}>
          .json
        </Button>
      </Stack>

      <DataGridPro
        rows={filtered}
        columns={columns}
        getRowId={(r) => r.id}
        density="compact"
        hideFooter
        disableColumnFilter
        disableColumnMenu
        disableRowSelectionOnClick
        getRowHeight={getRowHeight}
        onRowClick={(p) => toggleRow(Number(p.id))}
        sx={{ flex: 1, minHeight: 300, '& .MuiDataGrid-cell': { alignItems: 'flex-start' } }}
      />
    </Box>
  );
}
